import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useSnackbar } from 'notistack'
import palette from '../../../theme/palette'
import { filterButtonSx } from '../../common/sharedStyles'
import { useUpdateDefinitionMutation } from '../../redux/internalAlertsApiSlice'

const CADENCE = [{ value: 'NONE', label: 'No schedule' }, { value: 'DAILY', label: 'Daily' }, { value: 'WEEKLY', label: 'Weekly' }, { value: 'MONTHLY', label: 'Monthly' }]
const WEEKDAY_OPTS = [[1, 'Monday'], [2, 'Tuesday'], [3, 'Wednesday'], [4, 'Thursday'], [5, 'Friday'], [6, 'Saturday'], [7, 'Sunday']]
const FANOUT = [{ value: 'SINGLE', label: 'One per recipient' }, { value: 'MULTI', label: 'Several per recipient, 5 merchants each' }]

/**
 * Edit the human metadata of an alert: name, description, owner, expected cadence (drives the health check — it does
 * not schedule anything), fan-out shape. The backend diffs and logs only what changed.
 */
export default function EditDefinitionForm({ def, onDone, onCancel }) {
  const { enqueueSnackbar } = useSnackbar()
  const c = def.expectedCadence || {}
  const [f, setF] = useState({
    displayName: def.displayName || '', description: def.description || '', ownerEmail: def.ownerEmail || '',
    cadenceType: c.type || 'NONE', windowStartIst: c.windowStartIst || '06:45', windowEndIst: c.windowEndIst || '08:30', dayOfMonth: c.dayOfMonth || 1, dayOfWeek: c.dayOfWeek || 1,
    messagesPerRun: def.messagesPerRun || 'SINGLE', reason: '',
  })
  const set = (patch) => setF(s => ({ ...s, ...patch }))
  const [update, { isLoading }] = useUpdateDefinitionMutation()

  const daily = f.cadenceType === 'DAILY', weekly = f.cadenceType === 'WEEKLY', monthly = f.cadenceType === 'MONTHLY'
  const windowBad = (daily || weekly || monthly) && f.windowStartIst && f.windowEndIst && f.windowEndIst <= f.windowStartIst
  const nameBad = !f.displayName.trim()
  const emailBad = f.ownerEmail.trim() && !f.ownerEmail.includes('@')
  const invalid = nameBad || windowBad || emailBad || ((daily || weekly) && (!f.windowStartIst || !f.windowEndIst)) || (monthly && (f.dayOfMonth < 1 || f.dayOfMonth > 28))

  const submit = async (e) => {
    e?.preventDefault?.()
    if (invalid) return
    const body = {
      templateName: def.id,
      displayName: f.displayName.trim(), description: f.description.trim(), ownerEmail: f.ownerEmail.trim(),
      messagesPerRun: f.messagesPerRun, reason: f.reason.trim() || undefined,
    }
    if (f.cadenceType === 'NONE') body.clearCadence = true
    else body.expectedCadence = { type: f.cadenceType, windowStartIst: f.windowStartIst || null, windowEndIst: f.windowEndIst || null, dayOfMonth: monthly ? Number(f.dayOfMonth) : null, dayOfWeek: weekly ? Number(f.dayOfWeek) : null }
    try {
      await update(body).unwrap()
      enqueueSnackbar('Alert details saved', { variant: 'success' })
      onDone?.()
    } catch (err) {
      enqueueSnackbar(err?.data?.message || err?.message || 'Could not save', { variant: err?.status === 409 ? 'warning' : 'error', autoHideDuration: 8000 })
    }
  }

  return (
    <Box component='form' onSubmit={submit} sx={{ p: 2, border: `1px solid ${palette.primary.light}`, borderRadius: '12px', bgcolor: '#F7FAFF', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography sx={{ color: palette.primary.main, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem' }}>Edit details</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.5 }}>
        <TextField size='small' label='Name' value={f.displayName} onChange={(e) => set({ displayName: e.target.value })} error={nameBad} inputProps={{ maxLength: 80 }} required />
        <TextField size='small' label='Owner email' value={f.ownerEmail} onChange={(e) => set({ ownerEmail: e.target.value })} error={Boolean(emailBad)} inputProps={{ maxLength: 120 }} />
        {(def.channel || 'WHATSAPP') === 'WHATSAPP' && (
          <TextField select size='small' label='Messages' value={f.messagesPerRun} onChange={(e) => set({ messagesPerRun: e.target.value })}>
            {FANOUT.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        )}
      </Box>
      <TextField size='small' label='Description' value={f.description} onChange={(e) => set({ description: e.target.value })} multiline minRows={2} inputProps={{ maxLength: 500 }} />
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField select size='small' label='Schedule' value={f.cadenceType} onChange={(e) => set({ cadenceType: e.target.value })} sx={{ width: 220 }}
          helperText='Expected send window — used for the health check'>
          {CADENCE.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        {weekly && (
          <TextField select size='small' label='Day' value={f.dayOfWeek} onChange={(e) => set({ dayOfWeek: e.target.value })} sx={{ width: 150 }}>
            {WEEKDAY_OPTS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </TextField>
        )}
        {(daily || weekly || monthly) && (
          <>
            <TextField size='small' type='time' label='Window from (IST)' value={f.windowStartIst} onChange={(e) => set({ windowStartIst: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} required={daily || weekly} />
            <TextField size='small' type='time' label='Window to (IST)' value={f.windowEndIst} onChange={(e) => set({ windowEndIst: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} error={Boolean(windowBad)} helperText={windowBad ? 'Must be after "from"' : ''} required={daily || weekly} />
          </>
        )}
        {monthly && (
          <TextField size='small' type='number' label='Day of month' value={f.dayOfMonth} onChange={(e) => set({ dayOfMonth: e.target.value })} inputProps={{ min: 1, max: 28 }} sx={{ width: 130 }} />
        )}
      </Box>
      <TextField size='small' label='Reason (optional)' value={f.reason} onChange={(e) => set({ reason: e.target.value })} inputProps={{ maxLength: 300 }} helperText='Recorded in the change log' />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button size='small' sx={filterButtonSx('reset')} onClick={onCancel} disabled={isLoading}>Cancel</Button>
        <Button size='small' type='submit' variant='contained' disabled={invalid || isLoading} sx={{ textTransform: 'none', fontWeight: 600 }}>{isLoading ? 'Saving…' : 'Save'}</Button>
      </Box>
    </Box>
  )
}
